import { useNavigate } from 'react-router-dom';
import { FileText, PieChart, TrendingUp, DollarSign, BarChart3, ArrowRight } from 'lucide-react';
import { MainLayout } from '@/components/MainLayout';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useReportTemplates } from '@/hooks/useReportTemplates';

const templateIcons: Record<string, React.ReactNode> = {
  'monthly-cashflow': <FileText className="h-8 w-8" />,
  'expenses-by-center': <PieChart className="h-8 w-8" />,
  'receivables-summary': <DollarSign className="h-8 w-8" />,
  'income-vs-expense': <TrendingUp className="h-8 w-8" />,
  'cash-flow-trend': <BarChart3 className="h-8 w-8" />,
};

export function CustomReportTemplates() {
  const navigate = useNavigate();
  const templates = useReportTemplates();

  const handleUseTemplate = (templateId: string) => {
    navigate(`/reports/custom/new?template=${templateId}`);
  };

  return (
    <MainLayout>
      <SEO
        title="Templates de Relatórios"
        description="Comece com um template pré-configurado e personalize conforme suas necessidades"
      />
      <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Templates de Relatórios</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Comece com um template pré-configurado e personalize conforme suas necessidades
        </p>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card
            key={template.id}
            className="group hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer"
            onClick={() => handleUseTemplate(template.id)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="p-3 rounded-lg bg-primary/10 text-primary">
                  {templateIcons[template.id] || <FileText className="h-8 w-8" />}
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </div>
              <CardTitle className="mt-4">{template.name}</CardTitle>
              <CardDescription>{template.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Visualização:</span>
                  <span className="font-medium capitalize">
                    {template.config.visualizationType === 'bar' && 'Gráfico de Barras'}
                    {template.config.visualizationType === 'line' && 'Gráfico de Linhas'}
                    {template.config.visualizationType === 'pie' && 'Gráfico de Pizza'}
                    {template.config.visualizationType === 'table' && 'Tabela'}
                    {template.config.visualizationType === 'cards' && 'Cards'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Agrupamento:</span>
                  <span className="font-medium capitalize">
                    {template.config.groupBy === 'category' && 'Categoria'}
                    {template.config.groupBy === 'cost_center' && 'Centro de Custo'}
                    {template.config.groupBy === 'client' && 'Cliente'}
                    {template.config.groupBy === 'month' && 'Mês'}
                    {!template.config.groupBy && 'Nenhum'}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full mt-4 group-hover:bg-primary group-hover:text-primary-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUseTemplate(template.id);
                }}
              >
                Usar Template
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Custom Report CTA */}
      <Card className="border-dashed bg-muted/30">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            Quer criar do zero?
          </h3>
          <p className="text-muted-foreground mb-6 text-center max-w-md">
            Crie um relatório completamente personalizado escolhendo cada configuração
          </p>
          <Button onClick={() => navigate('/reports/custom/new')}>
            Criar Relatório Personalizado
          </Button>
        </CardContent>
      </Card>
    </div>
    </MainLayout>
  );
}
